import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useCallback } from 'react'
import { useNavigate } from 'react-router'
import { classroomsAtom, currentClassIdAtom, sessionsAtom, currentSessionIdAtom, sidebarOpenAtom } from '../state/store'
import type { ChatSession, ChatMessage } from '../state/types'
import { generateId, backendChatToSession } from '../state/types'
import { createChat, createClassChat, deleteChat } from '../api/backend'
import { useAuth } from '../contexts/AuthContext'
import { useWorkspace } from '../contexts/WorkspaceContext'
import { useWebSocket } from '../contexts/WebSocketContext'

/**
 * Pure state/action hook for session management.
 * Safe to call from multiple components — contains no side effects.
 *
 * Navigation-driven: selectSession/createSession/deleteSession use
 * router.push to change the URL. The URL-atom sync is handled by
 * useChatRouteSync in the route page components.
 *
 * Side-effect initialization (fetch, WS handlers, persist) lives in
 * useSessionsInit() which must be called exactly once in the chat layout.
 */
export function useSessions() {
  const [sessions, setSessions] = useAtom(sessionsAtom)
  const classrooms = useAtomValue(classroomsAtom)
  const currentClassId = useAtomValue(currentClassIdAtom)
  const currentSessionId = useAtomValue(currentSessionIdAtom)
  const setSidebarOpen = useSetAtom(sidebarOpenAtom)
  const { getAccessToken } = useAuth()
  const { memberRole } = useWorkspace()
  const { unsubscribe } = useWebSocket()
  const navigate = useNavigate()

  const createSession = useCallback(async (): Promise<ChatSession> => {
    if (currentClassId && memberRole === 'ADMIN') {
      throw new Error('Admins cannot create class chats.')
    }

    const token = getAccessToken()
    if (!token) {
      const session: ChatSession = {
        id: generateId(),
        name: 'New Chat',
        createdAt: Date.now(),
        lastUsedAt: Date.now(),
        classId: currentClassId,
        classTitle: classrooms.find((item) => item.id === currentClassId)?.title ?? null,
        messages: [],
      }
      setSessions((prev) => [session, ...prev])
      navigate(currentClassId ? `/classes/${currentClassId}/chats/${session.id}` : `/c/${session.id}`)
      return session
    }

    const { chat } = currentClassId
      ? await createClassChat(token, currentClassId)
      : await createChat(token)
    const session = backendChatToSession({
      ...chat,
      classId: chat.classId ?? currentClassId ?? null,
      classTitle: chat.classTitle ?? classrooms.find((item) => item.id === currentClassId)?.title ?? null,
    })
    setSessions((prev) => [session, ...prev])
    // Navigate to new chat — useChatRouteSync handles atom + WS subscribe
    navigate(session.classId ? `/classes/${session.classId}/chats/${session.id}` : `/c/${session.id}`)
    return session
  }, [getAccessToken, setSessions, navigate, currentClassId, classrooms, memberRole])

  const selectSession = useCallback(
    (sessionId: string) => {
      // Skip if already on this session
      if (sessionId === currentSessionId) return

      const session = sessions.find((item) => item.id === sessionId)
      // Navigate — useChatRouteSync handles atom update + WS subscribe/unsubscribe
      navigate(session?.classId ? `/classes/${session.classId}/chats/${sessionId}` : `/c/${sessionId}`)

      if (window.innerWidth < 768) {
        setSidebarOpen(false)
      }
    },
    [currentSessionId, navigate, setSidebarOpen, sessions],
  )

  const deleteSession = useCallback(
    async (sessionId: string) => {
      const token = getAccessToken()
      if (token) {
        try {
          await deleteChat(sessionId, token)
        } catch (err) {
          console.error('Failed to delete chat:', err)
        }
      }
      unsubscribe(sessionId)
      const session = sessions.find((item) => item.id === sessionId)
      setSessions((prev) => prev.filter((s) => s.id !== sessionId))
      if (currentSessionId === sessionId) {
        navigate(session?.classId ? `/classes/${session.classId}` : '/')
      }
    },
    [getAccessToken, unsubscribe, setSessions, currentSessionId, navigate, sessions],
  )

  const prependMessages = useCallback(
    (sessionId: string, msgs: ChatMessage[], hasMore: boolean) => {
      setSessions((prev) =>
        prev.map((s) => {
          if (s.id !== sessionId) return s
          // Deduplicate by id — older messages prepended before existing ones
          const existingIds = new Set(s.messages.map((m) => m.id))
          const newMsgs = msgs.filter((m) => !existingIds.has(m.id))
          return { ...s, messages: [...newMsgs, ...s.messages], hasMore }
        }),
      )
    },
    [setSessions],
  )

  const addMessage = useCallback(
    (sessionId: string, message: ChatMessage) => {
      setSessions((prev) =>
        prev.map((s) => {
          if (s.id !== sessionId) return s
          const updated = {
            ...s,
            lastUsedAt: Date.now(),
            messages: [...s.messages, message],
          }
          if (!s.preview && message.role === 'user') {
            updated.preview = message.content.slice(0, 100)
            updated.name = message.content.slice(0, 50) || 'New Chat'
          }
          return updated
        }),
      )
    },
    [setSessions],
  )

  const updateLastMessage = useCallback(
    (sessionId: string, updater: (msg: ChatMessage) => ChatMessage) => {
      setSessions((prev) =>
        prev.map((s) => {
          if (s.id !== sessionId || s.messages.length === 0) return s
          const messages = [...s.messages]
          messages[messages.length - 1] = updater(messages[messages.length - 1])
          return { ...s, messages, lastUsedAt: Date.now() }
        }),
      )
    },
    [setSessions],
  )

  const updateLastAssistantMessage = useCallback(
    (sessionId: string, updater: (msg: ChatMessage) => ChatMessage) => {
      setSessions((prev) =>
        prev.map((s) => {
          if (s.id !== sessionId || s.messages.length === 0) return s
          const messages = [...s.messages]
          for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i].role !== 'assistant') continue
            messages[i] = updater(messages[i])
            return { ...s, messages, lastUsedAt: Date.now() }
          }
          return s
        }),
      )
    },
    [setSessions],
  )

  const updateActiveAssistantMessage = useCallback(
    (sessionId: string, updater: (msg: ChatMessage) => ChatMessage) => {
      setSessions((prev) =>
        prev.map((s) => {
          if (s.id !== sessionId || s.messages.length === 0) return s
          const messages = [...s.messages]
          for (let i = messages.length - 1; i >= 0; i--) {
            const message = messages[i]
            if (message.role !== 'assistant') continue
            if (!message.isStreaming && !message.isGeneratingImage) continue
            messages[i] = updater(message)
            return { ...s, messages, lastUsedAt: Date.now() }
          }
          return s
        }),
      )
    },
    [setSessions],
  )

  const editMessage = useCallback(
    (sessionId: string, messageId: string, newContent: string) => {
      setSessions((prev) =>
        prev.map((s) => {
          if (s.id !== sessionId) return s
          const msgIndex = s.messages.findIndex((m) => m.id === messageId)
          if (msgIndex === -1) return s
          const truncated = s.messages.slice(0, msgIndex)
          const editedMsg = { ...s.messages[msgIndex], content: newContent }
          return { ...s, messages: [...truncated, editedMsg], lastUsedAt: Date.now() }
        }),
      )
    },
    [setSessions],
  )

  const updateMessageById = useCallback(
    (sessionId: string, messageId: string, updater: (msg: ChatMessage) => ChatMessage) => {
      setSessions((prev) =>
        prev.map((s) => {
          if (s.id !== sessionId) return s
          const msgIndex = s.messages.findIndex((m) => m.id === messageId)
          if (msgIndex === -1) return s
          const messages = [...s.messages]
          messages[msgIndex] = updater(messages[msgIndex])
          return { ...s, messages, lastUsedAt: Date.now() }
        }),
      )
    },
    [setSessions],
  )

  const deleteLastAssistantMessage = useCallback(
    (sessionId: string) => {
      setSessions((prev) =>
        prev.map((s) => {
          if (s.id !== sessionId) return s
          const lastMsg = s.messages[s.messages.length - 1]
          if (lastMsg?.role !== 'assistant') return s
          return { ...s, messages: s.messages.slice(0, -1), lastUsedAt: Date.now() }
        }),
      )
    },
    [setSessions],
  )

  const voteMessage = useCallback(
    (sessionId: string, messageId: string, vote: 'up' | 'down' | null) => {
      setSessions((prev) =>
        prev.map((s) => {
          if (s.id !== sessionId) return s
          return {
            ...s,
            messages: s.messages.map((m) =>
              m.id === messageId ? { ...m, vote: vote ?? undefined } : m,
            ),
          }
        }),
      )
    },
    [setSessions],
  )

  return {
    sessions,
    currentSessionId,
    createSession,
    selectSession,
    deleteSession,
    prependMessages,
    addMessage,
    updateLastMessage,
    updateLastAssistantMessage,
    updateActiveAssistantMessage,
    updateMessageById,
    editMessage,
    deleteLastAssistantMessage,
    voteMessage,
  }
}
